import { Textbox } from "fabric";
import React, { useEffect, useState } from "react";
import { CloseButton } from "react-bootstrap";
import { ModalType, useModal } from "../../Context/ModalContext";
import { TextColor, TextFont, TextSize } from "../../data/cardProperties";
import { useAppDispatch, useAppSelector } from "../../hook/redux";
import { Canvas } from "fabric/*";
import { defineTooBigFontSize, handleAutoResize } from "../Canvas/createCanvasText";


interface TextPropertiesSelectorModalProps {
  canvas: Canvas;
  option?: string;
}

function TextPropertiesSelectorModal( { canvas }: TextPropertiesSelectorModalProps ) {
  const { toolbarDisplay, limitFontSize, maxSizes, scale } = useAppSelector((state) => state.editor);

  const [ initialValue, setInitialValue ] = useState( "" );
  const [exampleText, setExampleText] = useState("")

  const { show, toggleShow, type } = useModal();

  const newIndex = canvas.getActiveObject();

  const dispatch = useAppDispatch();

  /**
   * A function that dynamically renders a list of items based on the provided modal type.
   * The function renders the appropriate UI based on the type of modal being displayed.
   * It supports rendering font options, text color options, and text size options.
   *
   * This function uses data mapping to generate an array of HTML `li` elements for the modal.
   * Each item is styled and assigned appropriate behavior according to its type.
   *
   * @returns {JSX.Element | null} A JSX fragment containing a list of items based on the modal type,
   * or null if the type doesn't match any predefined case.
   */
  const renderItems = (): React.JSX.Element[] | null => {
    switch (type) {
      case ModalType.font:
        return TextFont.map((font) => (
          <li
            key={ font.key }
            className={ `font-${ font.key } custom-modal-item custom-modal-font-item ${ initialValue && initialValue.split(',')[0].trim().replace(/['"]/g, '') === font.family.split(',')[0].trim().replace(/['"]/g, '') ?
                                                                                        "active" :
                                                                                        "" }` }
            style={ getLiStyle() }
            onClick={ () => {handleItemChange( font, canvas ), toggleShow(false, null, type)} }
          >
            {exampleText}
          </li>
        ));
      case ModalType.textColor:
        return TextColor.map((color) => (
          <li
            key={ color.value }
            className={ `custom-modal-item custom-modal-text-color-item ${ initialValue === color.value ?
                                                                           "active" :
                                                                           "" }` }
            style={ getLiStyle( color ) }
            onClick={ () => {handleItemChange( color, canvas ), toggleShow( false, null, type )} }
          />
        ));
      case ModalType.textSize:

        return TextSize.map((size) => {
          const disable = limitFontSize && Number(size.key) > Number(Math.round(limitFontSize / scale)) ;
          return (
            <li
              key={size.key}
              className={`custom-modal-item custom-modal-text-size-item ${initialValue === String(parseInt(size.key) * scale) ? "active" : ""}`}
              style={{
                ...getLiStyle(size),
                backgroundColor: disable ? "#d3d3d3" : "",
                color: disable ? "#a9a9a9" : "",
                cursor: disable ? "not-allowed" : "pointer",
                opacity: disable ? 0.5 : 1,
                pointerEvents: disable ? "none" : "auto",
                fontFamily: newIndex && (newIndex as Textbox).fontFamily ? (newIndex as Textbox).fontFamily : "Arial",
              }}
              onClick={() => {handleItemChange(size, canvas, disable), toggleShow(false, null, type)}}
            >
              {exampleText}
            </li>
          );
        });
      default:
        return null;
    }
  };


  /**
   * Handles the item change event and applies the required modifications based on the type of the item.
   * This function processes different types of modal actions (font, textColor, textSize) and applies
   * relevant changes to the canvas or related properties.
   *
   * @param {Object} item - An object containing details about the item.
   * @param {string} [item.key] - Represents an identifier or size value.
   * @param {string} [item.family] - The font family name.
   * @param {string} [item.ggFont] - Google Font identifier.
   * @param {string} [item.value] - Represents a color value or other item-specific data.
   * @param {string} [item.type] - The type of item change to handle (e.g., font, textColor, textSize).
   *
   * @param {Canvas} value - The canvas object where rendering updates are applied.
   *
   * @param {number|boolean|undefined} [disable] - A flag or value indicating whether certain actions, such as resizing
   *   or applying changes, should be skipped.
   */
  const handleItemChange = (item: { key?: string; family?: string; ggFont?: string; value?: string; type?: string; },
    value: Canvas,
    disable?: number | boolean | undefined) => {
    switch (type) {
      case ModalType.font:
        newIndex?.set({
          fontFamily: item.family,
          dirty: true,
        });
        value.renderAll();

        defineTooBigFontSize((newIndex as Textbox), dispatch);

        handleAutoResize((newIndex as Textbox));
        break;
      case ModalType.textColor:
        newIndex?.set({
          fill: item.value,
        });
        value.renderAll();
        break;
      case ModalType.textSize:
        if (disable) {
          return;
        }

        newIndex?.set({
          fontSize: Number(item.key) * scale,
        });
        value.renderAll();
        dispatch({
          type: "editor/updateActualMaxSizes",
          payload: {
            ...maxSizes,
            fontSize: Number(item.key) * scale,
          },
        });

        defineTooBigFontSize((newIndex as Textbox), dispatch);

        handleAutoResize((newIndex as Textbox));
        break;
      default:
        break;
    }
    toggleShow(false, null, type);
  };

  /**
   * Generates a style object for an item based on its type and the provided modal type.
   *
   * The function calculates and returns a style object customized depending on the current modal type
   * (`ModalType.textColor` or `ModalType.textSize`) and the properties of the passed item.
   * If no matching modal type is provided, it returns an empty object.
   *
   * @param {Object} [item] - Optional input object with properties affecting the generated style.
   *                          The expected structure and usage depend on the modal type:
   *                          - For `ModalType.textColor`: `item.type` should indicate whether
   *                            the item uses a gradient, while `item.value` specifies the color
   *                            or gradient value.
   *                          - For `ModalType.textSize`: `item.key` is used to compute the font size.
   * @returns {Object} - The computed style object based on the modal type and the item's properties:
   *                     - For `ModalType.textColor`: includes `background` and `border` properties.
   *                     - For `ModalType.textSize`: includes `fontSize`, `overflow`, `textOverflow`,
   *                       and `whiteSpace` properties.
   *                     - Returns an empty object if no valid modal type is matched.
   */
  const getLiStyle = (item?: any): object => {
    if (type === ModalType.textColor) {
      return {
        background: item && item.type === "gradient" ? `linear-gradient(${item.value})` : item.value,
        border: item && item.value === '#ffffff' ? "1px solid black" : "0px"
      }
    }
    if (type === ModalType.textSize) {
      return {
        fontSize: `${item.key * scale}px`,
        overflow: "hidden",
        textOverflow: "ellipsis",
        whiteSpace: "nowrap"
      }
    }
    return {};
  }

  /**
   * Handles click events on the background element.
   * This function is typically used to detect clicks outside of specific child elements
   * within a container and trigger an action when such a click is detected.
   *
   * @param {React.MouseEvent} e - The mouse event triggered by a click on the background element.
   *                               `e.target` refers to the element clicked, while `e.currentTarget`
   *                               refers to the element to which the event listener is attached.
   */
  const handleBackgroundClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget) {
      toggleShow(false, null, type);
    }
  };

  useEffect(() => {
    if (canvas && toolbarDisplay.index !== undefined) {
      switch (type) {
        case "font":
          setInitialValue((newIndex as Textbox)?.fontFamily);
          break;
        case "textColor":
          setInitialValue(((newIndex as Textbox)?.fill ?? "") as string);
          break;
        case "textSize":
          const currentSize = (newIndex as Textbox)?.fontSize;
          const closestSize = TextSize
          .map(s => parseInt(s.key) * scale)
          .reduce((prev, curr) =>
            Math.abs(curr - currentSize) < Math.abs(prev - currentSize) ? curr : prev
          );
          setInitialValue(String(closestSize));
          break;
        default:
          break;
      }
    }
  }, [show, type, canvas, toolbarDisplay.index, newIndex]);

  useEffect(() => {
      setExampleText(newIndex && (newIndex as Textbox).text ? (newIndex as Textbox).text : "Exemple de texte");
  }, [show, toolbarDisplay, canvas, newIndex]);

  return (
    <div 
      className={`custom-modal-background ${show &&
        [ModalType.font, ModalType.textColor, ModalType.textSize].includes(type) ? "" : "hidden"}`}
      onClick={handleBackgroundClick}
    >
            <div
        className={`custom-modal ${show &&
          [ModalType.font, ModalType.textColor, ModalType.textSize].includes(type) ? "" : "hidden"}`}
        aria-labelledby="contained-modal-title-vcenter"
      >
      <div className="custom-modal-content">
        <div className="custom-modal-header">
          <h4 className="custom-modal-title">
            {type === ModalType.font && "Choix de la police de caractère"}
            {type === ModalType.textColor && "Choix de la couleur de texte"}
            {type === ModalType.textSize && "Choix de la taille de caractère"}
          </h4>
          <CloseButton
            onClick={() => toggleShow(false, null, type)}
            aria-label="Close"
          />
        </div>

        <div className="custom-modal-body">
          <ul className="custom-modal-list">{renderItems()}</ul>
        </div>
      </div>
      </div>
    </div>
  );
}

export default TextPropertiesSelectorModal;